import { create } from "zustand";
import { ordresApi } from "@services/api/ordres";
import type { Ordre } from "types/ordre";

const PAGE_SIZE = 25;

/** Get a single ordre by ID from the store */
export function getOrdreById(id: string): Ordre | undefined {
  return useOrdreStore.getState().ordres.find((o) => o.id === id);
}

/** Find an ordre matching an appointment/job by plate */
export function findOrdreForPlate(plate: string): Ordre | undefined {
  return useOrdreStore.getState().ordres.find((o) => o.immatriculation === plate);
}

/** Build a default Ordre from an appointment or job for display */
export function buildOrdreFromAppointment(data: { clientName: string; plate: string; id: string }): Ordre {
  return {
    id: `ord-tmp-${data.id}`,
    org_id: "org1",
    num: `OR-${data.id.slice(0, 6).toUpperCase()}`,
    name: data.clientName,
    tags: [],
    status: "reception",
    immatriculation: data.plate,
    created_at: { seconds: Math.floor(Date.now() / 1000) },
    interventions: [],
    documents: [],
  };
}

interface OrdreStore {
  ordres: Ordre[];
  isLoading: boolean;
  isRefreshing: boolean;
  hasMore: boolean;
  hasPrev: boolean;
  page: number;
  statusFilter: string;
  sortBy: string;
  sortDir: string;
  myOnly: boolean;
  error: string | null;
  _fetchedOrgId: string | undefined;
  _assignedTo: string | undefined;

  fetchOrdres: (orgId: string, status?: string, assignedTo?: string) => Promise<void>;
  fetchNextPage: (orgId: string) => Promise<void>;
  fetchPrevPage: (orgId: string) => Promise<void>;
  setSort: (orgId: string, sortBy: string, sortDir: string) => Promise<void>;
  setMyOnly: (myOnly: boolean) => void;
  addOrdre: (ordre: Ordre) => void;
  updateOrdre: (ordre: Ordre) => void;
  removeOrdres: (ids: string[]) => void;
}

export const useOrdreStore = create<OrdreStore>((set) => ({
  ordres: [],
  isLoading: false,
  isRefreshing: false,
  hasMore: false,
  hasPrev: false,
  page: 0,
  statusFilter: "",
  sortBy: "created_at",
  sortDir: "desc",
  myOnly: false,
  error: null,
  _fetchedOrgId: undefined,
  _assignedTo: undefined,

  fetchOrdres: async (orgId: string, status?: string, assignedTo?: string) => {
    const normalizedStatus = status === "all" ? undefined : status;
    const state = useOrdreStore.getState();
    const showLoading = state.ordres.length === 0;
    set({ isLoading: showLoading, isRefreshing: true, error: null, page: 0, _fetchedOrgId: orgId, _assignedTo: assignedTo, statusFilter: normalizedStatus ?? "" });
    try {
      const { sortBy, sortDir } = useOrdreStore.getState();
      const ordres = await ordresApi.list({
        org_id: orgId,
        status: normalizedStatus,
        assigned_to: assignedTo,
        limit: PAGE_SIZE,
        offset: 0,
        sort_by: sortBy || undefined,
        sort_dir: sortDir,
      });
      set({
        ordres,
        isLoading: false,
        isRefreshing: false,
        page: 1,
        hasMore: ordres.length === PAGE_SIZE,
        hasPrev: false,
      });
    } catch (error: any) {
      console.error("Error fetching ordres:", error);
      set({ ordres: [], isLoading: false, isRefreshing: false, _fetchedOrgId: undefined, error: "Échec du chargement des ordres" });
    }
  },

  fetchNextPage: async (orgId: string) => {
    const { isLoading, hasMore, page, statusFilter, sortBy, sortDir, _assignedTo } = useOrdreStore.getState();
    if (isLoading || !hasMore) return;
    set({ isLoading: true });
    try {
      const next = await ordresApi.list({
        org_id: orgId,
        status: statusFilter || undefined,
        assigned_to: _assignedTo || undefined,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        sort_by: sortBy || undefined,
        sort_dir: sortDir,
      });
      set((state) => ({
        ordres: next,
        isLoading: false,
        page: state.page + 1,
        hasMore: next.length === PAGE_SIZE,
        hasPrev: true,
      }));
    } catch (error: any) {
      console.error("Error fetching next page:", error);
      set({ isLoading: false });
    }
  },

  fetchPrevPage: async (orgId: string) => {
    const { isLoading, hasPrev, page, statusFilter, sortBy, sortDir, _assignedTo } = useOrdreStore.getState();
    if (isLoading || !hasPrev) return;
    const prevPage = page - 2;
    set({ isLoading: true });
    try {
      const prev = await ordresApi.list({
        org_id: orgId,
        status: statusFilter || undefined,
        assigned_to: _assignedTo || undefined,
        limit: PAGE_SIZE,
        offset: prevPage * PAGE_SIZE,
        sort_by: sortBy || undefined,
        sort_dir: sortDir,
      });
      set((state) => ({
        ordres: prev,
        isLoading: false,
        page: state.page - 1,
        hasMore: true,
        hasPrev: prevPage > 0,
      }));
    } catch (error: any) {
      console.error("Error fetching prev page:", error);
      set({ isLoading: false });
    }
  },

  setSort: async (orgId: string, sortBy: string, sortDir: string) => {
    const { statusFilter } = useOrdreStore.getState();
    set({ sortBy, sortDir, _fetchedOrgId: undefined, isLoading: true, page: 0, hasPrev: false });
    try {
      const ordres = await ordresApi.list({
        org_id: orgId,
        status: statusFilter || undefined,
        limit: PAGE_SIZE,
        offset: 0,
        sort_by: sortBy || undefined,
        sort_dir: sortDir,
      });
      set({ ordres, isLoading: false, page: 1, hasMore: ordres.length === PAGE_SIZE, hasPrev: false });
    } catch (error: any) {
      set({ isLoading: false });
    }
  },

  setMyOnly: (myOnly: boolean) => {
    set({ myOnly, _fetchedOrgId: undefined });
  },

  addOrdre: (ordre: Ordre) => {
    set((state) => ({ ordres: [ordre, ...state.ordres], _fetchedOrgId: undefined }));
  },

  updateOrdre: (ordre: Ordre) => {
    set((state) => ({
      ordres: state.ordres.map((o) => (o.id === ordre.id ? ordre : o)),
    }));
  },

  removeOrdres: (ids: string[]) => {
    set((state) => ({
      ordres: state.ordres.filter((o) => !ids.includes(o.id)),
    }));
    ids.forEach((id) => {
      ordresApi.delete(id).catch((error) => {
        console.error("Error deleting ordre from API:", error);
      });
    });
  },
}));
